import type { ReactNode } from 'react';
import { AlertCircle, ArrowUpRight, Inbox, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { LaunchLoader } from '@/components/feedback/launch-loader';
import { Badge, Button, Card, CardContent } from '@/components/ui';
import { cn } from '@/lib/cn';
import { scoreVisualTone } from './score-visual';

export function PageIntro({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description: string;
  actions?: ReactNode;
}) {
  return (
    <header className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
      <div className="max-w-3xl">
        {eyebrow ? (
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-blue-600">
            {eyebrow}
          </p>
        ) : null}
        <h1 className="text-balance text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
          {title}
        </h1>
        <p className="mt-3 max-w-2xl text-pretty text-sm leading-6 text-slate-600 sm:text-base">
          {description}
        </p>
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}

export function FeatureLoading({ label }: { label: string }) {
  return (
    <div className="feature-loading-overlay" aria-busy="true">
      <LaunchLoader label={label} compact />
    </div>
  );
}

export function FeatureError({
  title,
  description,
  retryLabel,
  onRetry,
}: {
  title: string;
  description: string;
  retryLabel: string;
  onRetry?: () => void;
}) {
  return (
    <Card className="border-red-200 bg-red-50/70">
      <CardContent className="flex flex-col items-start gap-4 p-6 sm:flex-row sm:items-center">
        <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-red-100 text-red-700">
          <AlertCircle aria-hidden="true" size={20} />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-bold text-red-950">{title}</h2>
          <p className="mt-1 text-sm leading-6 text-red-800">{description}</p>
        </div>
        {onRetry ? (
          <Button variant="outline" onClick={onRetry}>
            <RefreshCw aria-hidden="true" size={16} />
            {retryLabel}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}

export function EmptyState({
  title,
  description,
  actionLabel,
  href,
  onAction,
}: {
  title: string;
  description: string;
  actionLabel: string;
  href?: string;
  onAction?: () => void;
}) {
  const button = (
    <>
      <ArrowUpRight aria-hidden="true" size={17} />
      {actionLabel}
    </>
  );
  return (
    <Card className="border-dashed bg-gradient-to-br from-white to-blue-50/50">
      <CardContent className="grid min-h-64 place-items-center p-8 text-center">
        <div className="max-w-md">
          <span className="mx-auto grid size-14 place-items-center rounded-2xl bg-blue-100 text-blue-700">
            <Inbox aria-hidden="true" size={24} />
          </span>
          <h2 className="mt-5 text-xl font-bold text-slate-950">{title}</h2>
          <p className="mt-2 text-sm leading-6 text-slate-600">{description}</p>
          <div className="mt-5">
            {href ? (
              <Link
                to={href}
                className="button button--primary button--md focus-ring inline-flex items-center gap-2 font-semibold"
              >
                {button}
              </Link>
            ) : (
              <Button onClick={onAction}>{button}</Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function ScoreRing({
  score,
  label,
  size = 'lg',
}: {
  score: number;
  label: string;
  size?: 'sm' | 'lg';
}) {
  const safe = Math.min(100, Math.max(0, Math.round(score)));
  const toneName = scoreVisualTone(safe);
  const tone = {
    success: '#059669',
    brand: '#2563eb',
    warning: '#d97706',
    danger: '#dc2626',
  }[toneName];
  return (
    <div
      className={cn(
        'relative grid shrink-0 place-items-center rounded-full',
        size === 'lg' ? 'size-36' : 'size-20',
      )}
      style={{ background: `conic-gradient(${tone} ${safe * 3.6}deg, #e2e8f0 0deg)` }}
      role="img"
      aria-label={`${label}: ${safe} / 100`}
    >
      <div
        className={cn(
          'flex flex-col items-center justify-center rounded-full bg-white text-center shadow-inner',
          size === 'lg' ? 'size-28' : 'size-16',
        )}
      >
        <strong
          className={cn(
            'grid min-h-10 place-items-center leading-none tabular-nums',
            `score-value--${toneName}`,
            size === 'lg' ? 'text-4xl' : 'text-xl',
          )}
        >
          {safe}
        </strong>
        <span className="mt-1 leading-none text-[10px] font-bold uppercase tracking-wider text-slate-500">
          / 100
        </span>
      </div>
    </div>
  );
}

export function SaveStatus({
  status,
  labels,
}: {
  status: 'idle' | 'dirty' | 'saving' | 'saved' | 'error';
  labels: Record<'dirty' | 'saving' | 'saved' | 'error', string>;
}) {
  if (status === 'idle') return null;
  const tone =
    status === 'saved'
      ? 'success'
      : status === 'error'
        ? 'danger'
        : status === 'dirty'
          ? 'warning'
          : 'brand';
  return <Badge tone={tone}>{labels[status === 'saving' ? 'saving' : status]}</Badge>;
}
